import React, { useState } from 'react';
import {
    Paper,
    Typography,
    FormControl,
    FormControlLabel,
    FormGroup,
    Switch,
    Box,
    Chip,
    Collapse,
    IconButton,
    TextField
} from '@mui/material';
import {
    VisibilityOutlined as VisibilityIcon,
    BusinessOutlined as BusinessIcon,
    CategoryOutlined as CategoryIcon,
    FlagOutlined as FlagIcon,
    DraftsOutlined as DraftIcon,
    AccountTreeOutlined as NodesIcon,
    ReceiptLongOutlined as ReceiptIcon,
    CreditCardOutlined as CardIcon
} from '@mui/icons-material';
import CollapsiblePanel from './CollapsiblePanel';

// hasCardRows: some row was charged on a card; the Cardholder column switch
// shows only then (the column itself is gated the same way).
const TransactionDisplayOptionsForm = ({ displayOptions, setDisplayOptions, hasCardRows = false }) => {


    const isPrintChartDisabled = !displayOptions?.printTransactions || false;
    const isPrintTransactionsDisabled = !displayOptions?.printChart || false;

    const handleOptionChange = (option) => (event) => {
        setDisplayOptions(prev => ({
            ...prev,
            [option]: event.target.checked
        }));
    };

    const handleMaxNodesChange = (event) => {
        const value = parseInt(event.target.value, 10);
        if (!isNaN(value) && value >= 0 && value <= 99) {
            setDisplayOptions(prev => ({
                ...prev,
                maxNodesPerType: value
            }));
        } else if (event.target.value === '') {
            // Allow empty value for temporary state while typing
            setDisplayOptions(prev => ({
                ...prev,
                maxNodesPerType: ''
            }));
        }
    };

    const titleElement = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon color="primary" fontSize="small"/>
            <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, display: 'flex',
                        alignItems: 'center',
                        gap: 1 }}>
                Display Options
            </Typography>
        </Box>
    );

    return (
        <CollapsiblePanel defaultCollapsed={true} titleElement={titleElement}>
            <FormControl component="fieldset" variant="standard">
                    <FormGroup sx={{ paddingLeft: '16px' }}>
                        {/* Document Status - Compact */}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={displayOptions.isDraft}
                                    onChange={handleOptionChange('isDraft')}
                                    color="primary"
                                    size="small"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DraftIcon sx={{ fontSize: 18 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Show as Draft
                                    </Typography>
                                    <Chip
                                        label={displayOptions.isDraft ? 'Draft' : 'Final'}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                </Box>
                            }
                            sx={{ mb: 0.5, '& .MuiFormControlLabel-label': { marginLeft: '6px' } }}
                        />

                        {/* Print Chart - Compact */}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={displayOptions.printChart}
                                    onChange={handleOptionChange('printChart')}
                                    disabled={isPrintChartDisabled}
                                    color="primary"
                                    size="small"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <NodesIcon sx={{ fontSize: 18 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Print Chart
                                    </Typography>
                                    <Chip
                                        label={displayOptions.printChart ? 'Yes' : 'No'}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                </Box>
                            }
                            sx={{ mb: 0.5, '& .MuiFormControlLabel-label': { marginLeft: '6px' } }}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={displayOptions.printTransactions}
                                    onChange={handleOptionChange('printTransactions')}
                                    disabled={isPrintTransactionsDisabled}
                                    color="primary"
                                    size="small"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ReceiptIcon sx={{ fontSize: 18 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Print Transaction Details
                                    </Typography>
                                    <Chip
                                        label={displayOptions.printTransactions ? 'Yes' : 'No'}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                </Box>
                            }
                            sx={{ mb: 0.5, '& .MuiFormControlLabel-label': { marginLeft: '6px' } }}
                        />

                        {/* Merchant Column - Compact */}
                        {displayOptions.printTransactions && (
                            <>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={displayOptions.showMerchant}
                                    onChange={handleOptionChange('showMerchant')}
                                    color="primary"
                                    size="small"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <BusinessIcon sx={{ fontSize: 18 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Show Merchant Column
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.2, ml: 0.2 }}>
                                        <Chip label="Merchant" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                    </Box>
                                </Box>
                            }
                            sx={{ mb: 0.5, '& .MuiFormControlLabel-label': { marginLeft: '6px' } }}
                        />

                        {/* Cardholder Column - Compact (card accounts only) */}
                        {hasCardRows && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={Boolean(displayOptions.showCardholder)}
                                    onChange={handleOptionChange('showCardholder')}
                                    color="primary"
                                    size="small"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <CardIcon sx={{ fontSize: 18 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Show Cardholder Column
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.2, ml: 0.2 }}>
                                        <Chip label="Cardholder" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                    </Box>
                                </Box>
                            }
                            sx={{ mb: 0.5, '& .MuiFormControlLabel-label': { marginLeft: '6px' } }}
                        />
                        )}

                        {/* Subcategory Column - Compact */}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={displayOptions.showSubcategory}
                                    onChange={handleOptionChange('showSubcategory')}
                                    color="primary"
                                    size="small"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <CategoryIcon sx={{ fontSize: 18 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Show Subcategory Column
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, ml: 0.2 }}>
                                        <Chip label="Subcategory" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                        {/* <Chip label="Respondent Source" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                        <Chip label="Marital Source" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} /> */}
                                    </Box>
                                </Box>
                            }
                            sx={{ mb: 0.5, '& .MuiFormControlLabel-label': { marginLeft: '6px' } }}
                        />

                        {/* Flag Column - Compact */}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={displayOptions.showFlag}
                                    onChange={handleOptionChange('showFlag')}
                                    color="primary"
                                    size="small"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <FlagIcon sx={{ fontSize: 18 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Show Flag Column
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, ml: 0.2 }}>
                                        <Chip label="Flag" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                        {/* <Chip label="Respondent Settlement" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} /> */}
                                    </Box>
                                </Box>
                            }
                            sx={{ mb: 0.5, '& .MuiFormControlLabel-label': { marginLeft: '6px' } }}
                        />
                            </>
                        )}

                        {/* Max Nodes Per Type - Number Input */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, pl: '6px' }}>
                            <NodesIcon sx={{ fontSize: 18 }} />
                            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 'fit-content' }}>
                                Display top
                            </Typography>
                            <TextField
                                type="number"
                                value={displayOptions.maxNodesPerType ?? ''}
                                onChange={handleMaxNodesChange}
                                size="small"
                                sx={{
                                    width: 80,
                                    ml: 1,
                                    '& .MuiInputBase-input': {
                                        fontSize: '0.875rem',
                                        padding: '4px 8px',
                                        textAlign: 'center'
                                    }
                                }}
                                inputProps={{
                                    min: 0,
                                    max: 99,
                                    step: 1
                                }}
                                variant="outlined"
                            />
                            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 'fit-content' }}>
                                Nodes Per Perspective Type (0 = all)
                            </Typography>
                            <Chip
                                label={`${displayOptions.maxNodesPerType === 0 ? 'All' : displayOptions.maxNodesPerType} nodes`}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                        </Box>
                    </FormGroup>
                </FormControl>
        </CollapsiblePanel>
    );
};

export default TransactionDisplayOptionsForm;